// src/features/tickets/services/TicketAnhangTextService.ts
//
// Lesezugriff auf den erkannten Text der Ticket-Anhänge
// (Tabelle ticket_anhang_texte, XTAX GmbH & Co. KG, seit 15.10.2026).
//
// Den Text schreibt ausschließlich der ticket-texterkennung-worker. Das
// Intranet liest nur:
//   - ladeStatus: Status je Anhang für die Anzeige im Ticket, ohne den Text
//     selbst, damit das Ticket schnell lädt
//   - ladeText:   den vollständigen Text eines Anhangs, erst beim Aufklappen
//
// Gefunden wird der Text in der Ticketsuche über die Datenbankfunktionen
// list_tickets_page und search_tickets; dafür ist hier nichts nötig.

import { supabase } from '../../../shared/services/supabase';

export type TicketAnhangTextStatus =
  | 'offen'
  | 'laufend'
  | 'fertig'
  | 'kein_text'
  | 'nicht_unterstuetzt'
  | 'zu_gross'
  | 'fehlgeschlagen';

export type TicketAnhangTextQuelle = 'textebene' | 'ocr' | 'office' | 'text' | 'zendesk';

/** Status der Texterkennung eines Anhangs (ohne den Text). */
export interface TicketAnhangTextInfo {
  attachment_id: string;
  status: TicketAnhangTextStatus;
  quelle: TicketAnhangTextQuelle | null;
  gekuerzt: boolean;
  letzter_fehler: string | null;
}

export interface TicketAnhangTextResult {
  success: boolean;
  text?: string;
  error?: string;
}

/**
 * Blockgröße für .in('attachment_id', ...). Hält die Abfrage-Adresse sicher
 * unter der Längengrenze des Servers (gleicher Wert wie beim Nachladen der
 * Ticketliste).
 */
const STATUS_CHUNK_SIZE = 100;

class TicketAnhangTextService {
  /**
   * Status der Texterkennung je Anhang. Anhänge ohne Eintrag fehlen im
   * Ergebnis. Bei einem Fehler wird ein leeres Ergebnis geliefert; die Anzeige
   * zeigt dann keinen Status statt eines falschen.
   */
  async ladeStatus(attachmentIds: string[]): Promise<Record<string, TicketAnhangTextInfo>> {
    const ergebnis: Record<string, TicketAnhangTextInfo> = {};
    if (attachmentIds.length === 0) return ergebnis;

    try {
      const bloecke: string[][] = [];
      for (let i = 0; i < attachmentIds.length; i += STATUS_CHUNK_SIZE) {
        bloecke.push(attachmentIds.slice(i, i + STATUS_CHUNK_SIZE));
      }

      const antworten = await Promise.all(bloecke.map(ids => supabase
        .from('ticket_anhang_texte')
        .select('attachment_id, status, quelle, gekuerzt, letzter_fehler')
        .in('attachment_id', ids)
        .limit(ids.length)
      ));

      for (const { data, error } of antworten) {
        if (error) {
          console.error('[TicketAnhangTextService.ladeStatus] Load-Error:', error);
          return {};
        }
        for (const zeile of (data ?? []) as TicketAnhangTextInfo[]) {
          ergebnis[zeile.attachment_id] = zeile;
        }
      }
      return ergebnis;
    } catch (error) {
      console.error('[TicketAnhangTextService.ladeStatus] Unexpected error:', error);
      return {};
    }
  }

  /** Vollständiger erkannter Text eines Anhangs. */
  async ladeText(attachmentId: string): Promise<TicketAnhangTextResult> {
    try {
      const { data, error } = await supabase
        .from('ticket_anhang_texte')
        .select('text')
        .eq('attachment_id', attachmentId)
        .maybeSingle();

      if (error) {
        console.error('[TicketAnhangTextService.ladeText] Load-Error:', error);
        return { success: false, error: error.message };
      }

      const text = (data as { text?: string | null } | null)?.text;
      if (typeof text !== 'string') {
        return { success: false, error: 'Für diesen Anhang liegt kein Text vor.' };
      }
      return { success: true, text };
    } catch (error) {
      console.error('[TicketAnhangTextService.ladeText] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
}

// Singleton-Export
export const ticketAnhangTextService = new TicketAnhangTextService();
